import { Component, Input, OnInit, ViewChild } from '@angular/core'
import { Store } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'
import { HubtypeQueue } from 'models/hubtype-queue'
import {
  HubtypeUser,
  STATUS_AVAILABLE,
  STATUS_AWAY,
  STATUS_BUSY,
  STATUS_OFFLINE,
} from 'models/hubtype-user'
import { first, takeUntil } from 'rxjs'
import { AngularComponent } from 'shared/AngularComponent'
import { HtButtonIntent } from 'shared/base/ht-button/ht-button.component'
import {
  HtModalComponent,
  HtModalOptions,
} from 'shared/base/ht-modal/ht-modal.component'
import { TableConfig } from 'shared/base/ht-table/ht-table.component'
import { AnalyticsEvents } from 'src/app/constants/analytics'
import { CustomError } from 'src/app/constants/errors'
import { FeedbackService } from 'src/app/services/feedback.service'
import { QueueService } from 'src/app/services/hubtype-api/queue.service'
import { Analytics } from 'utils/analytics'
import * as fromRoot from '../../reducers'
import { FilterUserStatus } from '../filter-user-status/filter-user-status.component'
import {
  USER_AVATAR_COLUMN,
  UserListComponent,
} from '../user-list/user-list.component'
import { DEFAULT_ACTIONS } from './../base/ht-table/ht-table.component'

@Component({
  selector: 'assign-agents-to-queue-modal',
  templateUrl: './assign-agents-to-queue-modal.component.html',
  styleUrls: ['./assign-agents-to-queue-modal.component.scss'],
  standalone: false,
})
export class AssignAgentsToQueueModalComponent
  extends AngularComponent
  implements OnInit
{
  @ViewChild('agentsTable') agentsTable: UserListComponent
  @ViewChild('modal') modal: HtModalComponent
  @Input() canUnassignAgents = false

  allAgents: HubtypeUser[] = []
  disabledAgentsIds: string[] = []
  modalOptions: HtModalOptions

  queue: HubtypeQueue
  searchFilter = ''
  selectedAgentsIds: string[] = []
  tableCheckboxFilter: string[] = [
    STATUS_AVAILABLE,
    STATUS_AWAY,
    STATUS_BUSY,
    STATUS_OFFLINE,
  ]
  tableConfig: TableConfig

  constructor(
    private store: Store<fromRoot.State>,
    private queueService: QueueService,
    private feedbackService: FeedbackService,
    private translate: TranslateService
  ) {
    super()
    this.modalOptions = {
      title: {
        text: this.translate.instant('shared.assignAgentsToQueueModal.title'),
      },
      rightBtnArray: [
        {
          show: true,
          text: this.translate.instant('common.cancel'),
          intent: HtButtonIntent.SECONDARY,
          click: () => this.close(),
        },
        {
          show: true,
          text: this.translate.instant(
            'shared.assignAgentsToQueueModal.assignAgents'
          ),
          intent: HtButtonIntent.PRIMARY,
          click: () => this.assignAgents(),
          disabled: true,
        },
      ],
    }
    this.tableConfig = {
      columns: [
        {
          ...USER_AVATAR_COLUMN,
          name: this.translate.instant('shared.assignAgentsToQueueModal.agent'),
        },
        {
          name: '',
          type: 'string',
          class: 'text-small text-secondary',
          computedLabel: (user: HubtypeUser) =>
            this.disabledAgentsIds.includes(user.id)
              ? this.translate.instant(
                  'shared.assignAgentsToQueueModal.alreadyAssigned'
                )
              : '',
        },
      ],
      actions: [
        {
          ...DEFAULT_ACTIONS.DELETE,
          labelKey: 'shared.assignAgentsToQueueModal.unassignAgent',
          onClick: agent => {
            this.unassignAgent(agent)
          },
          isDeactivated: agent =>
            !this.canUnassignAgents ||
            !this.disabledAgentsIds.includes(agent.id),
        },
      ],
      selectable: true,
      onRowSelected: (selectedRows: HubtypeUser[]) =>
        this.updateRightButtonStatus(selectedRows.length),
    }
  }

  onClose() {
    this.disabledAgentsIds = []
    this.queue = null
    this.searchFilter = ''
  }

  close() {
    this.modal.close()
  }

  ngOnInit(): void {}

  onSearch(text: string) {
    this.searchFilter = text
  }

  onStatusFilterChange(checkboxFilter: FilterUserStatus[]) {
    this.tableCheckboxFilter = checkboxFilter
      .filter(checkbox => checkbox.checked)
      .map(status => status.status)
  }

  get searchPlaceholder() {
    return this.translate.instant(
      'shared.assignAgentsToQueueModal.searchPlaceholder'
    )
  }

  open(queue: HubtypeQueue) {
    const selectedAgentsIds = queue.agents.map(({ id }) => id)
    this.getAllAgents()
    this.queue = queue
    this.disabledAgentsIds = [...selectedAgentsIds]
    this.selectedAgentsIds = [...selectedAgentsIds]
    this.updateRightButtonStatus(queue.agents.length)
    this.modalOptions.title.text = this.translate.instant(
      'shared.assignAgentsToQueueModal.titleWithQueue',
      { queueName: queue.name }
    )
    this.modal.open()
  }

  private assignAgents() {
    const agents = this.agentsTable.getSelectedUsers()
    this.queueService
      .addAgents(this.queue, agents)
      .pipe(first())
      .subscribe({
        complete: () => {
          Analytics.event(AnalyticsEvents.MANAGE_QUEUE_AGENTS_MODAL_ASSIGN, {
            assignedAgents: agents.length,
          })
          this.close()
          this.feedbackService.success(
            this.translate.instant(
              agents.length > 1
                ? 'shared.assignAgentsToQueueModal.agentsAssigned'
                : 'shared.assignAgentsToQueueModal.agentAssigned'
            )
          )
        },
        error: err =>
          this.feedbackService.error(
            CustomError.USER_QUEUE_ASSIGN.code,
            this.translate.instant(CustomError.USER_QUEUE_ASSIGN.messageKey),
            err
          ),
      })
  }

  private getAllAgents() {
    this.store
      .select(fromRoot.agents)
      .pipe(first(), takeUntil(this.ngUnsubscribe))
      .subscribe(agents => {
        this.allAgents = agents
      })
  }

  private updateRightButtonStatus(queueAgentsCount: number) {
    const newAgentsLength = queueAgentsCount - this.disabledAgentsIds.length
    const rightBtn = this.modalOptions.rightBtnArray[1]
    if (newAgentsLength === 0) {
      rightBtn.text = this.translate.instant(
        'shared.assignAgentsToQueueModal.assignAgents'
      )
      rightBtn.disabled = true
    } else {
      rightBtn.text = this.translate.instant(
        'shared.assignAgentsToQueueModal.assignAgentsCount',
        { count: newAgentsLength }
      )
      rightBtn.disabled = false
    }
  }

  private unassignAgent(agent: HubtypeUser) {
    this.queueService
      .deleteAgents(this.queue, [agent])
      .pipe(first())
      .subscribe({
        complete: () => {
          Analytics.event(AnalyticsEvents.MANAGE_QUEUE_AGENTS_MODAL_UNASSIGN)
          this.feedbackService.success(
            this.translate.instant(
              'shared.assignAgentsToQueueModal.agentUnassigned'
            )
          )
          this.setAgentAsDeleted(agent)
        },
        error: err =>
          this.feedbackService.error(
            CustomError.USER_QUEUE_UNASSIGN.code,
            this.translate.instant(CustomError.USER_QUEUE_UNASSIGN.messageKey),
            err
          ),
      })
  }

  private setAgentAsDeleted(agent: HubtypeUser) {
    this.disabledAgentsIds = this.disabledAgentsIds.filter(
      id => id !== agent.id
    )
    this.selectedAgentsIds = this.selectedAgentsIds.filter(
      id => id !== agent.id
    )
  }
}
